import type { MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { canCreateDataSource, canEditDataModel, useEntitlement } from '#layers/access/public';

/**
 * The "no collections yet" empty state, which `ContentEmptyState` and the Content sidebar render
 * and so must agree on. Shaped as `DEmpty`'s prop bag so either can bind it directly.
 *
 * Its call to action lands on Data Model → Sources, reachable through either entitlement: adding a
 * source or modelling an existing one both end the empty state.
 *
 * @param description Replaces the description shown to users who may act on the empty state.
 */
export function useContentEmptyStateGuard(description?: MaybeRefOrGetter<string | undefined>) {
	const { t } = useI18n();
	const { can } = useEntitlement();

	const allowed = computed(() => can(canEditDataModel) || can(canCreateDataSource));

	return computed(() => ({
		title: t('pages.content.empty.title'),
		description: allowed.value
			? toValue(description) ?? t('pages.content.empty.description')
			: t('pages.content.empty.description_restricted'),
		actionButtonLabel: allowed.value ? t('pages.content.empty.action') : undefined,
	}));
}
